import graphqlClient from './graphqlClient';
import { gql } from 'graphql-request';

// Session cache helpers
const getSessionCache = <T>(key: string): T | null => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) return JSON.parse(cached) as T;
  } catch { /* ignore */ }
  return null;
};

const setSessionCache = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch { /* ignore */ }
};

export interface GeographicalData {
  states: string[];
  counties: string[];
  cities: string[];
}

const FETCH_STATES_QUERY = gql`
  query FetchStates {
    udm_geo(distinct_on: state, order_by: { state: asc }, where: { state: { _is_null: false } }) {
      state
    }
  }
`;

const FETCH_COUNTIES_QUERY = gql`
  query FetchCounties($state: String!) {
    udm_geo(
      distinct_on: county
      order_by: { county: asc }
      where: { county: { _is_null: false }, state: { _eq: $state } }
    ) {
      county
    }
  }
`;

const FETCH_CITIES_QUERY = gql`
  query FetchCities($state: String!, $county: String) {
    udm_geo(
      distinct_on: city
      order_by: { city: asc }
      where: {
        _and: [
          { city: { _is_null: false } }
          { state: { _eq: $state } }
          { county: { _is_null: true } }
        ]
      }
    ) {
      city
    }
  }
`;

interface StatesResponse {
  udm_geo: Array<{ state: string | null }>;
}

interface CountiesResponse {
  udm_geo: Array<{ county: string | null }>;
}

interface CitiesResponse {
  udm_geo: Array<{ city: string | null }>;
}

/**
 * Capitalize first letter of a string
 */
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Fetch all states from geo table
 * @returns Array of state names
 */
export const fetchStates = async (): Promise<string[]> => {
  const cacheKey = 'geo-cache-states';
  const cached = getSessionCache<string[]>(cacheKey);
  if (cached) return cached;

  try {
    const response = await graphqlClient.request<StatesResponse>(FETCH_STATES_QUERY);

    const result = response.udm_geo
      .map((item) => item.state)
      .filter((state): state is string => state !== null && state !== undefined)
      .map(capitalizeFirstLetter)
      .sort();

    setSessionCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error fetching states:', error);
    return [];
  }
};

/**
 * Fetch counties for a given state
 * @param state - State to fetch counties for
 * @returns Array of county names
 */
export const fetchCountiesByState = async (state: string): Promise<string[]> => {
  try {
    const response = await graphqlClient.request<CountiesResponse>(FETCH_COUNTIES_QUERY, {
      state: state.toLowerCase(),
    });

    return response.udm_geo
      .map((item) => item.county)
      .filter((county): county is string => county !== null && county !== undefined)
      .map(capitalizeFirstLetter)
      .sort();
  } catch (error) {
    console.error('Error fetching counties for state:', error);
    return [];
  }
};

/**
 * Fetch cities for a given state and county
 * @param state - State to fetch cities for
 * @param county - Optional county to filter cities
 * @returns Array of city names
 */
export const fetchCitiesByStateAndCounty = async (
  state: string,
  county?: string
): Promise<string[]> => {
  try {
    const response = await graphqlClient.request<CitiesResponse>(FETCH_CITIES_QUERY, {
      state: state.toLowerCase(),
      county: county ? county.toLowerCase() : null,
    });

    return response.udm_geo
      .map((item) => item.city)
      .filter((city): city is string => city !== null && city !== undefined)
      .map(capitalizeFirstLetter)
      .sort();
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
};

/**
 * Fetch all cities grouped by state
 * @returns Object with state names as keys and array of cities as values
 */
export const fetchAllCitiesByState = async (): Promise<Record<string, string[]>> => {
  const cacheKey = 'geo-cache-cities-by-state';
  const cached = getSessionCache<Record<string, string[]>>(cacheKey);
  if (cached) return cached;

  try {
    const query = gql`
      query FetchAllCities {
        udm_geo(
          distinct_on: [state, city]
          order_by: [{ state: asc }, { city: asc }]
          where: {
            _and: [
              { city: { _is_null: false } }
              { state: { _is_null: false } }
              { city: { _neq: "unknown zipcode" } }
              { state: { _neq: "unknown zipcode" } }
            ]
          }
        ) {
          state
          city
        }
      }
    `;

    const response = await graphqlClient.request<{ udm_geo: Array<{ state: string | null; city: string | null }> }>(query);

    const cityMap: Record<string, string[]> = {};

    response.udm_geo.forEach((item) => {
      if (item.state && item.city) {
        const capitalizedState = capitalizeFirstLetter(item.state);
        const capitalizedCity = capitalizeFirstLetter(item.city);

        if (!cityMap[capitalizedState]) {
          cityMap[capitalizedState] = [];
        }

        if (!cityMap[capitalizedState].includes(capitalizedCity)) {
          cityMap[capitalizedState].push(capitalizedCity);
        }
      }
    });

    // Sort cities within each state
    Object.keys(cityMap).forEach((state) => {
      cityMap[state].sort();
    });

    setSessionCache(cacheKey, cityMap);
    return cityMap;
  } catch (error) {
    console.error('Error fetching all cities by state:', error);
    return {};
  }
};
